// API utility functions with JWT authentication

// Helper function to get API URL - handles both development and production
const getApiUrl = () => {
  const serverUrl = process.env.REACT_APP_SERVER_URL;
  const serverPort = process.env.REACT_APP_SERVER_PORT;
  
  if (!serverUrl) {
    throw new Error('REACT_APP_SERVER_URL environment variable is required');
  }

  let url = serverUrl;

  // If it's a production HTTPS URL or already includes a port, don't append port
  if (serverUrl.startsWith('https://') || serverUrl.includes(':')) {
    url = serverUrl;
  } else {
    // For development (HTTP without port), append the port
    if (serverPort) {
      url = `${serverUrl}:${serverPort}`;
    }
  }
  
  // Remove trailing slash to prevent double slashes when constructing endpoints
  return url.replace(/\/$/, '');
};

const API_URL = getApiUrl();

// Get JWT token from localStorage
const getToken = () => {
  return localStorage.getItem('authToken');
};

// Create headers with JWT token
const getAuthHeaders = () => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// Make authenticated GET request
export const apiGet = async (endpoint) => {
  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Request failed');
  }
  
  return response.json();
};

// Make authenticated POST request
export const apiPost = async (endpoint, data) => {
  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(data)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Request failed');
  }
  
  return response.json();
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const token = getToken();
  if (!token) return false;
  
  try {
    // Basic check - in production you might want to verify token expiry
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp > Date.now() / 1000;
  } catch {
    return false;
  }
};

// Logout function
export const logout = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('userRole');
  localStorage.removeItem('userId');
  localStorage.removeItem('userName');
  localStorage.removeItem('userEmail');
  
  // Dispatch custom event to update header
  window.dispatchEvent(new CustomEvent('authChange'));
  
  window.location.href = '/login';
};

// Get current user info from token
export const getCurrentUser = () => {
  const token = getToken();
  if (!token) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      id: payload.id,
      name: payload.name,
      email: payload.email,
      role_id: payload.role_id
    };
  } catch {
    return null;
  }
}; 

// Reset password email utility
export const resetPasswordEmail = async (email) => {
  try {
    const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send reset email');
    }

    return data;
  } catch (error) {
    console.error('Reset password email error:', error);
    throw error;
  }
};

// Reset password with token utility
export const resetPasswordWithToken = async (token, newPassword) => {
  try {
    const response = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, newPassword }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to reset password');
    }

    return data;
  } catch (error) {
    console.error('Reset password error:', error);
    throw error;
  }
}; 